import { Box, Button, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import type { JSX } from "react";

import type { OttoBotWithSecret } from "../types.js";

/**
 * Shown ONCE after a successful create — the embed key + signing secret with
 * copy buttons. Closing the dialog loses the secret forever. Extracted from
 * `BotsPanel.tsx`.
 */
export function SecretRevealDialog({
  bot,
  onClose,
}: {
  bot: OttoBotWithSecret;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "1.125rem", fontWeight: 600 }}>Bot created — save these secrets now</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
          The signing secret is shown ONCE. Closing this dialog loses it forever.
        </Typography>
        <Box sx={{ mt: 2, display: "grid", gap: 1.5 }}>
          <KeyRow label="Embed key" value={bot.embed_key} />
          <KeyRow label="Signing secret" value={bot.signing_secret} mono />
        </Box>
        <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end" }}>
          <Button type="button" variant="contained" onClick={onClose}>
            I've saved these — close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function KeyRow({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>{label}</Typography>
      <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          component="code"
          sx={{
            flex: 1,
            overflowX: "auto",
            borderRadius: 0.5,
            bgcolor: "surfaceSoft",
            p: 1,
            fontSize: 12,
            ...(mono ? { fontFamily: "ui-monospace, monospace" } : {}),
          }}
        >
          {value}
        </Box>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          size="small"
          onClick={() => navigator.clipboard.writeText(value)}
        >
          Copy
        </Button>
      </Box>
    </Box>
  );
}
